import { CSmartTable } from '@coreui/react-pro'
import Edit from '@mui/icons-material/Edit'
import TimelineIcon from '@mui/icons-material/Timeline'
import { CircularProgress, Dialog, IconButton, Box, Typography } from '@mui/material'
import debounce from 'lodash/debounce'
import dayjs from 'dayjs'
import { useCallback, useContext, useEffect, useState } from 'react'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import { useNavigate, useParams } from 'react-router-dom'
import ClinicHeader from 'src/clinic/components/ClinicHeader'
import { EditPatientDialogComponent } from 'src/clinic/components/ClinicManageBookingsPage/ClinicManageBookingComponents'
import { getCPDataWithParams } from 'src/clinic/services'
import { PATIENT_INFO_OBJECT } from 'src/clinic/views/patientListing/patient-listing-utils'
import { CustomInput } from 'src/consumer/components/helperComponents'
import { GenericHeaderBackButton } from 'src/consumer/components/UserActivityPage/MyPlanComponents'
import { GENDER_LIST_OFFLINE, ROLE_TYPES } from 'src/consumer/constants'
import { getAgeFromDobWithTime } from 'src/consumer/helpers/profilePageHelper'
import { ClinicDataContext } from 'src/context/ClinicDataContext'
import { getAllRolesInBoolean } from 'src/mentor/layout/utils'
import { PharmacySearchContainer } from '../pharmacyDatabase/styledComponents'
import ImportPatientsDialog from './components/ImportPatientsDialog'
import {
  PatientListingContainerForClinic,
  PatientListingPageContainer,
} from './patientListingStyles'

export default function PatientListingPage() {
  const { clinicId } = useParams()
  const { chosenRoleFlow, ownerData, assistantData } = useContext(ClinicDataContext)

  let thisClinicData
  if (chosenRoleFlow === ROLE_TYPES.roleClinicOwner) {
    thisClinicData = ownerData?.associatedClinicsData?.find((item) => item?.id === clinicId)
  } else if (chosenRoleFlow === ROLE_TYPES.roleClinicAssistant) {
    thisClinicData = assistantData?.associatedClinicsData?.find((item) => item?.id === clinicId)
  }

  const [showImportDialog, setShowImportDialog] = useState(false)

  function openImportDialog() {
    setShowImportDialog(true)
  }

  function closeImportDialog() {
    setShowImportDialog(false)
  }

  return (
    <>
      <ClinicHeader greetingsEnabled={true} />
      <GenericHeaderBackButton
        clickfunction={() => window.history.back()}
        styles={{ margin: '20px' }}
      />
      <PatientListingPageComponent
        clinicLocationId={thisClinicData?.id}
        clinicBrandId={thisClinicData?.clinicBrandId}
        clinicLocationName={thisClinicData?.name}
        canEditPatient={true}
        getPatientListApiSignature="/api/secure/ca/patient/list"
      />
      <Dialog open={showImportDialog} onClose={closeImportDialog}>
        <ImportPatientsDialog
        // refreshData={setup}
        />
      </Dialog>
    </>
  )
}

const columns = [
  {
    key: 'patientNumber',
    label: 'Patient Number',
    _style: { minWidth: '140px' },
    filter: false,
    sorter: false,
  },
  {
    key: 'name',
    label: 'Name',
    _style: { minWidth: '250px' },
    filter: false,
    sorter: false,
  },
  {
    key: 'mobile',
    label: 'Contact',
    _style: { minWidth: '180px' },
    filter: false,
    sorter: false,
  },
  {
    key: 'alternateContacts',
    label: 'Alternate Contacts',
    _style: { minWidth: '200px' },
    filter: false,
    sorter: false,
  },
  {
    key: 'createdTime',
    label: 'Created',
    minWidth: 200,
    _style: { minWidth: '120px' },
    filter: false,
    sorter: false,
  },
  {
    key: 'actions',
    label: '',
    maxWidth: 50,
    _style: { minWidth: '50px' },
    filter: false,
    sorter: false,
  },
]

function scopedColumns(handleEdit, navigate, canEdit) {
  return {
    patientNumber: (item) => (
      <td>
        <span style={{ 
          fontFamily: 'monospace', 
          fontWeight: 600,
          color: item?.patientNumber ? 'rgba(67, 56, 202, 0.9)' : 'rgba(0, 0, 0, 0.4)'
        }}>
          {item?.patientNumber || 'Not assigned'}
        </span>
      </td>
    ),
    name: (item) => {
      const age = getAgeFromDobWithTime(item?.dateOfBirth)
      const gender = item?.gender
        ? GENDER_LIST_OFFLINE?.find((genderItem) => genderItem?.value === item?.gender)?.label || ''
        : null
      
      return (
        <td>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '14px',
                color: 'rgba(0, 0, 0, 0.87)',
                lineHeight: 1.4,
              }}
            >
              {item?.name || '-'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {age && (
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: 'rgba(0, 0, 0, 0.6)',
                    fontWeight: 500,
                  }}
                >
                  {age}
                </Typography>
              )}
              {gender && (
                <>
                  {age && (
                    <span style={{ color: 'rgba(0, 0, 0, 0.3)', fontSize: '12px' }}>•</span>
                  )}
                  <Typography
                    sx={{
                      fontSize: '12px',
                      color: 'rgba(0, 0, 0, 0.6)',
                      fontWeight: 500,
                    }}
                  >
                    {gender}
                  </Typography>
                </>
              )}
            </Box>
          </Box>
        </td>
      )
    },
    mobile: (item) => (
      <td>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {item?.mobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography
                sx={{
                  fontSize: '13px',
                  color: 'rgba(0, 0, 0, 0.87)',
                  fontWeight: 500,
                }}
              >
                {item.mobile}
              </Typography>
            </Box>
          )}
          {item?.email && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography
                sx={{
                  fontSize: '12px',
                  color: 'rgba(0, 0, 0, 0.6)',
                }}
              >
                {item.email}
              </Typography>
            </Box>
          )}
          {!item?.mobile && !item?.email && (
            <Typography
              sx={{
                fontSize: '13px',
                color: 'rgba(0, 0, 0, 0.4)',
                fontStyle: 'italic',
              }}
            >
              No contact info
            </Typography>
          )}
        </Box>
      </td>
    ),
    alternateContacts: (item) => {
      const altMobiles = Array.isArray(item?.altMobiles) ? item.altMobiles.filter(Boolean) : []
      const altEmails = Array.isArray(item?.altEmails) ? item.altEmails.filter(Boolean) : []
      const hasAlternateContacts = altMobiles.length > 0 || altEmails.length > 0

      return (
        <td>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {altMobiles.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                <Typography
                  sx={{
                    fontSize: '11px',
                    color: 'rgba(0, 0, 0, 0.5)',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Alt Mobiles
                </Typography>
                {altMobiles.map((altMobile, index) => (
                  <Typography
                    key={index}
                    sx={{
                      fontSize: '12px',
                      color: 'rgba(0, 0, 0, 0.7)',
                      fontWeight: 500,
                    }}
                  >
                    {altMobile}
                  </Typography>
                ))}
              </Box>
            )}
            {altEmails.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                <Typography
                  sx={{
                    fontSize: '11px',
                    color: 'rgba(0, 0, 0, 0.5)',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Alt Emails
                </Typography>
                {altEmails.map((altEmail, index) => (
                  <Typography
                    key={index}
                    sx={{
                      fontSize: '12px',
                      color: 'rgba(0, 0, 0, 0.7)',
                    }}
                  >
                    {altEmail}
                  </Typography>
                ))}
              </Box>
            )}
            {!hasAlternateContacts && (
              <Typography
                sx={{
                  fontSize: '12px',
                  color: 'rgba(0, 0, 0, 0.4)',
                  fontStyle: 'italic',
                }}
              >
                None
              </Typography>
            )}
          </Box>
        </td>
      )
    },
    actions: (item) => (
      <td>
        {canEdit && (
          <IconButton 
            onClick={() => handleEdit(item)}
            size="small"
            sx={{
              color: 'rgba(67, 56, 202, 0.7)',
              '&:hover': {
                color: 'rgba(67, 56, 202, 1)',
                backgroundColor: 'rgba(67, 56, 202, 0.08)',
              },
            }}
          >
            <Edit />
          </IconButton>
        )}
        {(getAllRolesInBoolean().isDoctor || getAllRolesInBoolean().isTeacher) && (
          <IconButton 
            onClick={() => navigate(item?.id)}
            size="small"
            sx={{
              color: 'rgba(67, 56, 202, 0.7)',
              '&:hover': {
                color: 'rgba(67, 56, 202, 1)',
                backgroundColor: 'rgba(67, 56, 202, 0.08)',
              },
            }}
          >
            <TimelineIcon />
          </IconButton>
        )}
      </td>
    ),
    createdTime: (item) => (
      <td>
        <span>{item?.createdTime ? dayjs(item?.createdTime)?.format('DD-MMM-YYYY') : ''}</span>
      </td>
    ),
  }
}

export const PatientListingPageComponent = ({
  clinicLocationId,
  clinicBrandId,
  clinicLocationName,
  canEditPatient,
  getPatientListApiSignature = '/api/secure/mentor/patient/list',
}) => {
  const navigate = useNavigate()
  const [selectedPatient, setSelectedPatient] = useState()

  const [search, setSearch] = useState('')
  const availableRoles = getAllRolesInBoolean()

  function handleEdit(item) {
    setSelectedPatient(item)
  }

  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [loadingPatients, setLoadingPatients] = useState(false)
  const [hasMorePatients, setHasMorePatients] = useState(true)
  const [patientsList, setPatientsList] = useState()

  async function getNextPage() {
    getMorePatientsList(page + 1)
    setLoadingPatients(false)
  }

  async function setup() {
    setLoading(true)
    var promises = []
    promises = [...promises, getPatientList()]
    await Promise.all(promises)
    setLoading(false)
  }

  const [sentryRef] = useInfiniteScroll({
    loading: loadingPatients,
    hasNextPage: hasMorePatients,
    onLoadMore: getNextPage,
    rootMargin: '0px 0px 400px 0px',
  })

  useEffect(() => {
    if (
      availableRoles.isDoctor ||
      availableRoles.isClinicOwner ||
      availableRoles.isPharmacist ||
      availableRoles.isClinicAssistant
    ) {
      setup()
    } else {
      navigate('/aumhum-for-clinics')
    }
  }, [])

  async function getPatientList(pageToUse = 1, searchToUse) {
    const resp = await getCPDataWithParams(getPatientListApiSignature, {
      clinicId: clinicLocationId,
      clinicBrandId: clinicBrandId,
      page: pageToUse,
      pageSize: 10,
      search: searchToUse,
    })
    if (resp.status === 200) {
      if (resp.data && resp.data.length > 0) {
        setPatientsList(resp.data.map((data) => ({ ...PATIENT_INFO_OBJECT, ...data })))
        setPage(pageToUse)
      } else {
        setHasMorePatients(resp.data.length < 0 ? false : true)
      }
    }
  }

  async function getMorePatientsList(nextPage, searchToUse = search) {
    const resp = await getCPDataWithParams(getPatientListApiSignature, {
      clinicId: clinicLocationId,
      clinicBrandId: clinicBrandId,
      page: nextPage,
      pageSize: 10,
      search: searchToUse,
    })
    if (resp.status === 200) {
      setPage(nextPage)
      if (resp.data && resp.data.length > 0) {
        setPatientsList((prev) => [
          ...(prev || []),
          ...(resp.data.map((data) => ({ ...PATIENT_INFO_OBJECT, ...data })) || []),
        ])
      } else {
        setHasMorePatients(false)
      }
    }
  }

  async function searchFunction(e) {
    setLoading(true)
    setSearch(e.target.value)
  }

  const debouncedSearch = useCallback(
    debounce(async (value) => {
      await getPatientList(1, value)
      setLoading(false)
    }, 500),
    [],
  )

  useEffect(() => {
    debouncedSearch(search)
  }, [search])

  return (
    <PatientListingPageContainer>
      <Box
        sx={{
          padding: { xs: 2, sm: 3 },
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
          marginBottom: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 2,
            marginBottom: 2,
          }}
        >
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: 'rgba(67, 56, 202, 0.95)',
                fontSize: { xs: '20px', sm: '24px' },
                marginBottom: 0.5,
              }}
            >
              Patients
            </Typography>
            {clinicLocationName && (
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(0, 0, 0, 0.6)',
                  fontSize: '14px',
                }}
              >
                {clinicLocationName}
              </Typography>
            )}
          </Box>
          <PharmacySearchContainer sx={{ minWidth: { xs: '100%', sm: '300px' } }}>
            <CustomInput 
              defaultValue={search} 
              onChange={searchFunction} 
              placeholder="Search patients by name, mobile, or patient number..." 
            />
          </PharmacySearchContainer>
        </Box>
      </Box>
      <Box
        sx={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
          overflow: 'hidden',
        }}
      >
        <PatientListingContainerForClinic>
          <CSmartTable
            items={patientsList || []}
            columns={columns}
            loading={loading}
            tableProps={{
              hover: true,
              striped: false,
              responsive: true,
              sx: {
                '& .table': {
                  marginBottom: 0,
                },
                '& thead th': {
                  backgroundColor: 'rgba(67, 56, 202, 0.06)',
                  color: 'rgba(67, 56, 202, 0.9)',
                  fontWeight: 600,
                  fontSize: '14px',
                  borderBottom: '2px solid rgba(67, 56, 202, 0.1)',
                  padding: '12px 16px',
                },
                '& tbody tr': {
                  transition: 'background-color 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(67, 56, 202, 0.04)',
                  },
                },
                '& tbody td': {
                  padding: '12px 16px',
                  fontSize: '14px',
                  borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
                },
              },
            }}
            pagination={false}
            itemsPerPage={10000000000000000}
            scopedColumns={scopedColumns(handleEdit, navigate, canEditPatient)}
          />
          {(loadingPatients || hasMorePatients) && !loading && (
            <Box
              ref={sentryRef}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '20px',
                gap: 1,
                width: '100%',
              }}
            >
              <CircularProgress sx={{ color: 'rgba(67, 56, 202, 0.7)' }} size={24} />
              <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>
                Loading more patients...
              </Typography>
            </Box>
          )}
        </PatientListingContainerForClinic>
      </Box>
      <EditPatientDialogComponent
        dialogTitle="Patient Details"
        open={Boolean(selectedPatient)}
        handleCloseDialog={() => setSelectedPatient(null)}
        patientId={selectedPatient?.id}
        clinicId={clinicLocationId}
        clinicBrandId={selectedPatient?.clinicBrandId}
        patientSearchApiSignature={getPatientListApiSignature}
      />
    </PatientListingPageContainer>
  )
}
